import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterOutlet } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { FeatureFlagDirective, FeatureFlagService } from '@examdojo/core/feature-flag';
import { IconComponent } from '@examdojo/core/icon';
import { DebuggingContextService, DebuggingWindowComponent, IssueReportFabComponent } from '@examdojo/debugging';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { PlatformService } from '@examdojo/platform';
import { QuestionAttemptStoreModel } from '@examdojo/question';
import { AppLoaderComponent, RoutingLoadingService } from '@examdojo/ui/loading';
import { IonApp } from '@ionic/angular/standalone';
import { translateSignal } from '@jsverse/transloco';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { fromResize } from '@examdojo/core/rxjs';
import { register as registerSwiper } from 'swiper/element/bundle';
import { InformativeBannerComponent } from '@examdojo/ui/informative-banner';
import { IfWideLayoutDirective } from '@examdojo/layout';
import { DatasetUploadDialogService } from './debugging/dataset-upload-dialog/dataset-upload-dialog.service';
import { APP_EFFECTS } from './effects';
import { IntercomService } from './intercom/intercom.service';

registerSwiper();

@Component({
  selector: 'dojo-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    RouterOutlet,
    DebuggingWindowComponent,
    FeatureFlagDirective,
    MatProgressBar,
    IconComponent,
    MatTooltip,
    AppLoaderComponent,
    IssueReportFabComponent,
    InformativeBannerComponent,
    IfWideLayoutDirective,
  ],
  providers: [...APP_EFFECTS],
  animations: [
    getAnimationAppearDisappearWithHeight({ enter: AnimationDuration.Medium, leave: AnimationDuration.Long }),
  ],
})
export class AppComponent {
  constructor(
    private readonly routingLoadingService: RoutingLoadingService,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly datasetUploadDialogService: DatasetUploadDialogService,
    private readonly platformService: PlatformService,
  ) {
    this.observeInfoBannerHeight()
      .pipe(
        takeUntilDestroyed(),
        tap((height) => this.infoBannerHeight.set(height)),
      )
      .subscribe();
  }

  private readonly desktopInfoBannerContainer = viewChild<ElementRef<HTMLElement>>('desktopInfoBannerContainer');
  private readonly infoBannerHeight = signal(0);

  readonly isIpad = this.platformService.isTablet && this.platformService.isIos;

  private readonly instantiatedServices = [
    ...APP_EFFECTS,
    IntercomService,
    FeatureFlagService<ExamdojoFeatureFlags>,
  ].map((service) => inject(service));

  /**
   * By design, Transloco will only load the scopes when they are actually used, not when being provided.
   * This comes with the limitation of not being able to translate programmatically when the scope is not loaded, typically in toasts.
   * Do not remove.
   * @see https://github.com/jsverse/transloco/issues/820
   */
  protected readonly preloadExamdojoTranslationsLoading = translateSignal('');

  protected readonly ExamDojoFeatureFlag = ExamDojoFeatureFlag;

  readonly state = connectState({
    showFullScreenLoader: this.routingLoadingService.showFullScreenLoader$,
    showLoadingBar: this.routingLoadingService.showLoadingBar$,
    infoBannerHeight: toObservable(this.infoBannerHeight),
    gradedAttemptId: this.debuggingContextService.context$.pipe(
      map((context) => {
        const attempt = context['Attempt'] as QuestionAttemptStoreModel | undefined;
        return attempt?.id && attempt?.status === 'GRADED' ? attempt.id : null;
      }),
      distinctUntilChanged(),
    ),
  });

  openDatasetUploadDialog(attemptId: QuestionAttemptStoreModel['id']) {
    this.datasetUploadDialogService.openDialog({ input: { attemptId } });
  }

  private observeInfoBannerHeight() {
    return toObservable(this.desktopInfoBannerContainer).pipe(
      filter(Boolean),
      map((container) => container.nativeElement),
      switchMap((element) => fromResize(element, { emitOnStart: true })),
      map((rect) => rect.height),
    );
  }
}
